import type { ReactNode } from 'react';
import RemoveRedEyeOutlined from '@mui/icons-material/RemoveRedEyeOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

export interface PreviewPaneProps {
  /** Card heading (translated), e.g. "Preview". */
  title: string;
  /** Caption under the preview (translated), e.g. the sample-data note. */
  caption?: string;
  /** Optional refresh action (label translated). */
  refreshLabel?: string;
  onRefresh?: () => void;
  /** True while a server render is in flight — dims the surface. */
  loading?: boolean;
  children: ReactNode;
}

/**
 * The dashed preview surface used by the template editor and the notification
 * settings screen: a paper card with an eye heading, optional refresh, and the
 * rendered content inside a dashed container.
 */
export function PreviewPane({
  title,
  caption,
  refreshLabel,
  onRefresh,
  loading = false,
  children,
}: PreviewPaneProps) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: '10px', p: 2.25 }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RemoveRedEyeOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          {onRefresh ? (
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshOutlined />}
              onClick={onRefresh}
              sx={{ ml: 'auto' }}
            >
              {refreshLabel}
            </Button>
          ) : null}
        </Box>
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: '10px',
            p: 1.5,
            bgcolor: 'background.default',
            opacity: loading ? 0.6 : 1,
            transition: 'opacity 120ms',
          }}
        >
          {children}
        </Box>
        {caption ? (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{caption}</Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

export interface EmailPreviewFrameProps {
  /** Server-rendered email HTML (the preview endpoint's `bodyHtml`). */
  html: string;
  /** Rendered subject line; when given, shown above the frame. */
  subject?: string | null;
  /** "Subject:" label (translated). */
  subjectLabel?: string;
  /** Frame height in px. */
  height?: number;
  /** Accessible frame title (translated). */
  title: string;
}

/**
 * Sandboxed iframe for server-rendered email HTML — no scripts, no top-level
 * navigation. The subject renders as a chrome row above the document.
 */
export function EmailPreviewFrame({
  html,
  subject,
  subjectLabel,
  height = 460,
  title,
}: EmailPreviewFrameProps) {
  return (
    <Stack spacing={1.25}>
      {subject !== undefined ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'baseline',
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            borderRadius: '8px',
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
            }}
          >
            {subjectLabel}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
            {subject}
          </Typography>
        </Box>
      ) : null}
      <Box
        component="iframe"
        title={title}
        sandbox=""
        srcDoc={html}
        sx={{
          width: '100%',
          height,
          border: 1,
          borderColor: 'divider',
          borderRadius: '10px',
          bgcolor: '#fff',
          display: 'block',
        }}
      />
    </Stack>
  );
}

export interface SmsPreviewBubbleProps {
  /** Rendered plain-text SMS body. */
  text: string;
  /** From-line caption under the bubble (translated), e.g. sender + provider note. */
  caption?: string;
}

/** Phone-style SMS bubble for plain-text previews (prototype `NteSmsPreview`). */
export function SmsPreviewBubble({ text, caption }: SmsPreviewBubbleProps) {
  return (
    <Box sx={{ bgcolor: 'background.default', borderRadius: '12px', p: 2.25 }}>
      <Box
        sx={(theme) => ({
          maxWidth: 300,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: '16px 16px 16px 4px',
          px: 1.875,
          py: 1.375,
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        })}
      >
        {text}
      </Box>
      {caption ? (
        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 1 }}>{caption}</Typography>
      ) : null}
    </Box>
  );
}
